import { Box, Typography, TypographyProps } from '@mui/material';
import 'katex/dist/katex.min.css';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import TypingIndicatorComponent from './typeIndicator';

interface ShowMoreMarkdownProps {
  content: string;
  previewLines: number; // The number of lines to show in the preview
  streamInProgress?: boolean;
}

const ShowMoreMarkdown = ({
  content,
  previewLines,
  streamInProgress,
}: ShowMoreMarkdownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  content = content.replace(/\\\[(.*?)\\\]/gs, '$$$$ $1 $$$$');

  // Render only previewLines in the initial view
  const contentPreviewArr = content.split('\n').slice(0, previewLines);
  const hasMoreContent = content.split('\n').length > previewLines;
  if (hasMoreContent) {
    contentPreviewArr[contentPreviewArr.length - 1] = `${contentPreviewArr[
      contentPreviewArr.length - 1
    ]
      .split(' ')
      .slice(
        0,
        Math.floor(
          contentPreviewArr[contentPreviewArr.length - 1].split(' ').length / 2,
        ),
      )
      .join(' ')}...`;
  }
  const contentPreview = contentPreviewArr.join('\n');

  return (
    <Box sx={{ overflowY: 'auto' }}>
      <ReactMarkdown
        //removed remarkGfm plugin
        remarkPlugins={[remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          h2: ({ ...props }) => (
            <Typography
              variant='h6'
              component='h6'
              gutterBottom
              {...(props as TypographyProps)}
              lineHeight='1.1'
              marginY={0.8}
            />
          ),
          h3: ({ ...props }) => (
            <Typography
              component='h6'
              fontSize={'1.1rem'}
              fontWeight={600}
              gutterBottom
              marginBlockStart='10px'
              marginBlockEnd='10px'
              {...(props as TypographyProps)}
              lineHeight='1.1'
              marginY={0.8}
            />
          ),
          h4: ({ ...props }) => (
            <Typography
              component='h6'
              fontSize={'1.1rem'}
              fontWeight={600}
              gutterBottom
              marginBlockStart='10px'
              marginBlockEnd='10px'
              {...(props as TypographyProps)}
              lineHeight='1.1'
              marginY={0.8}
            />
          ),
          p: ({ ...props }) => (
            <Typography
              variant='body1'
              paragraph
              {...(props as TypographyProps)}
              lineHeight='1.1'
              fontWeight={200}
              marginBottom={0.8}
            />
          ),
          ul: ({ ...props }) => (
            <Box
              component='ul'
              sx={{ paddingLeft: 3.75, marginY: 1.1 }}
              {...(props as React.ComponentProps<typeof Box>)}
            />
          ),
          ol: ({ ...props }) => (
            <Box
              component='ol'
              sx={{ paddingLeft: 3.75, marginY: 1.1 }}
              {...(props as React.ComponentProps<typeof Box>)}
              counterReset='item'
              listStyleType='none'
            />
          ),
          li: ({ ...props }) => (
            <Box
              component='li'
              sx={{
                'marginBottom': 0.6,
                'lineHeight': '1.1',
                'fontWeight': 200,
                '::marker': {
                  fontWeight: 600,
                },
                '&:has(ol) > &': {
                  'counterIncrement': 'item',
                  '&::before': {
                    content: 'counter(item) ". "',
                    display: 'inline-block',
                    listStyleType: 'none',
                    fontWeight: 600,
                  },
                },
              }}
              {...(props as React.ComponentProps<typeof Box>)}
              counterReset='li'
              conterIncrement='li'
            />
          ),
          strong: ({ ...props }) => (
            <Box
              component='strong'
              sx={{
                fontWeight: 600,
                lineHeight: '1.1',
                marginBottom: 0,
              }}
              {...(props as React.ComponentProps<typeof Box>)}
            />
          ),
          table: ({ ...props }) => (
            <Box sx={{ overflowX: 'auto' }}>
              <Box
                component='table'
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
                {...(props as React.ComponentProps<typeof Box>)}
              />
            </Box>
          ),
          th: ({ ...props }) => (
            <Box
              component='th'
              sx={{
                border: '1px solid #d3d3d3',
                padding: '8px',
                lineHeight: '1.1',
              }}
              {...(props as React.ComponentProps<typeof Box>)}
            />
          ),
          td: ({ ...props }) => {
            return (
              <Box
                component='td'
                sx={{
                  border: '1px solid #d3d3d3',
                  padding: '8px',
                  fontWeight: 200,
                  lineHeight: '1.1',
                  width: 'max-content',
                  textAlign: Number.isNaN(
                    Number(props?.children?.toString()?.replace(/[,₹%]/g, '')),
                  )
                    ? 'left'
                    : 'right',
                }}
                {...(props as React.ComponentProps<typeof Box>)}
              />
            );
          },
        }}
      >
        {isExpanded ? content : contentPreview}
      </ReactMarkdown>
      {hasMoreContent && (
        <span
          style={{ color: '#1084ff', cursor: 'pointer' }}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </span>
      )}
      {streamInProgress && (
        <Box paddingY={1}>
          <TypingIndicatorComponent />
        </Box>
      )}
    </Box>
  );
};

export default ShowMoreMarkdown;
